/**
 * SELVANE Variant Submit Fix (v4.0 - Fetch + XHR + String body)
 * Goal: Force correct variant `id` before /cart/add request is sent.
 *
 * Works for:
 * - native form POST (/cart/add)
 * - fetch('/cart/add' or '/cart/add.js') with FormData / URLSearchParams / string body
 * - XMLHttpRequest (jQuery.ajax)
 *
 * Debug:
 *   window.__SELVANE_VARIANT_FIX_DEBUG__ = true;
 */

(function () {
  "use strict";

  // ---------- utils ----------
  function debugEnabled() {
    return !!window.__SELVANE_VARIANT_FIX_DEBUG__;
  }
  function log(...a) {
    if (debugEnabled()) console.log("[SELVANE VariantFix v4.0]", ...a);
  }
  function warn(...a) {
    if (debugEnabled()) console.warn("[SELVANE VariantFix v4.0]", ...a);
  }
  function normalize(s) {
    return String(s == null ? "" : s).trim();
  }
  function safeJSON(text) {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }
  function isCartAddUrl(url) {
    const s = String(url || "");
    return s.includes("/cart/add");
  }
  function extractHandleFromPath(path) {
    const m = String(path || "").match(/\/products\/([^/?#]+)/i);
    return m ? m[1] : "";
  }

  // ---------- variants ----------
  function looksLikeVariantsArray(data) {
    return (
      Array.isArray(data) &&
      data.length &&
      data[0] &&
      typeof data[0] === "object" &&
      "id" in data[0]
    );
  }

  function getVariantsFromDOMNear(el) {
    const root =
      (el && el.closest && el.closest("section, .product, .product-main, .main-product, body")) ||
      document;

    const scripts = Array.from(root.querySelectorAll('script[type="application/json"]'));
    for (const s of scripts) {
      const data = safeJSON((s.textContent || "").trim());
      if (looksLikeVariantsArray(data)) return data;
    }

    // global fallback
    const all = Array.from(document.querySelectorAll('script[type="application/json"]'));
    for (const s of all) {
      const data = safeJSON((s.textContent || "").trim());
      if (looksLikeVariantsArray(data)) return data;
    }
    return null;
  }

  async function getVariantsByHandle(handle) {
    if (!handle) return null;
    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
      if (!res.ok) return null;
      const p = await res.json();
      if (p && Array.isArray(p.variants)) return p.variants;
      return null;
    } catch {
      return null;
    }
  }

  function variantOptionsArray(v) {
    if (!v) return [];
    if (Array.isArray(v.options) && v.options.length) return v.options.map(normalize);
    const out = [v.option1, v.option2, v.option3].map(normalize).filter(Boolean);
    if (out.length) return out;
    if (v.title) {
      return String(v.title)
        .split("/")
        .map((x) => normalize(x))
        .filter(Boolean);
    }
    return [];
  }

  function matchVariantId(variants, selected) {
    const sel = (selected || []).map(normalize).filter(Boolean);
    if (!sel.length) return null;

    // strict order match
    for (const v of variants) {
      const vOpts = variantOptionsArray(v);
      if (vOpts.length !== sel.length) continue;
      let ok = true;
      for (let i = 0; i < sel.length; i++) {
        if (vOpts[i] !== sel[i]) { ok = false; break; }
      }
      if (ok) return String(v.id);
    }

    // order-insensitive unique match
    if (sel.length >= 2) {
      const matches = variants.filter((v) => {
        const vOpts = variantOptionsArray(v);
        if (vOpts.length !== sel.length) return false;
        return sel.every((x) => vOpts.includes(x));
      });
      if (matches.length === 1) return String(matches[0].id);
    }

    return null;
  }

  // ---------- extract selected options from "payload" ----------
  function extractOptionsFromKeyValue(getterFn) {
    // standard options[Color] etc
    const values = [];

    // A) options[...]
    const keys = getterFn("__keys__") || [];
    for (const k of keys) {
      if (typeof k === "string" && k.startsWith("options[")) {
        const v = normalize(getterFn(k));
        if (v) values.push(v);
      }
    }
    if (values.length) return { mode: "standard", values };

    // B) loose fields: Color/Size
    const colorKeys = ["Color","Colour","color","colour","颜色","顏色"];
    const sizeKeys  = ["Size","size","尺码","尺碼","尺寸"];

    let color = "";
    for (const k of colorKeys) {
      const v = normalize(getterFn(k));
      if (v) color = v;
    }
    let size = "";
    for (const k of sizeKeys) {
      const v = normalize(getterFn(k));
      if (v) size = v;
    }

    const loose = [];
    if (color) loose.push(color);
    if (size) loose.push(size);

    if (loose.length) return { mode: "loose", values: loose, color, size };

    return { mode: "none", values: [] };
  }

  // ---------- resolve variantId ----------
  async function resolveVariantId({ nearEl, handleHint, getterFn }) {
    let variants = getVariantsFromDOMNear(nearEl);
    if (!variants) {
      const handle = handleHint || extractHandleFromPath(window.location.pathname);
      variants = await getVariantsByHandle(handle);
    }
    if (!variants) return null;

    const opt = extractOptionsFromKeyValue(getterFn);
    if (!opt.values.length) return null;

    const vid = matchVariantId(variants, opt.values);
    log("resolveVariantId:", { mode: opt.mode, selected: opt.values, vid });
    return vid;
  }

  // ---------- helpers to mutate bodies ----------
  function setFormIdInput(form, vid) {
    const input = form.querySelector('[name="id"]');
    if (!input) return false;
    input.value = String(vid);
    input.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  }

  function parseQueryStringBody(bodyStr) {
    const usp = new URLSearchParams(String(bodyStr || ""));
    return usp;
  }

  function urlSearchParamsToString(usp) {
    return usp.toString();
  }

  // ---------- 1) native submit / click capture ----------
  async function fixFormBeforeSubmit(form) {
    try {
      const fd = new FormData(form);

      // build getterFn for FormData
      const keys = [];
      for (const [k] of fd.entries()) keys.push(k);

      const getterFn = (k) => {
        if (k === "__keys__") return keys;
        return fd.get(k);
      };

      const beforeId = normalize(fd.get("id"));
      const vid = await resolveVariantId({
        nearEl: form,
        handleHint: extractHandleFromPath(window.location.pathname),
        getterFn,
      });

      if (!vid) {
        warn("No vid resolved; keep id:", beforeId);
        return;
      }
      if (beforeId !== vid) {
        setFormIdInput(form, vid);
        log("Updated form id:", { from: beforeId, to: vid });
      }
    } catch (e) {
      warn("fixFormBeforeSubmit error:", e);
    }
  }

  document.addEventListener(
    "submit",
    function (e) {
      const form = e.target && e.target.closest ? e.target.closest('form[action*="/cart/add"]') : null;
      if (!form) return;
      fixFormBeforeSubmit(form);
    },
    true
  );

  document.addEventListener(
    "click",
    function (e) {
      const btn = e.target && e.target.closest ? e.target.closest('button[type="submit"], input[type="submit"]') : null;
      if (!btn) return;
      const form = btn.closest && btn.closest('form[action*="/cart/add"]');
      if (!form) return;
      fixFormBeforeSubmit(form);
    },
    true
  );

  // ---------- 2) patch fetch ----------
  (function patchFetch() {
    const orig = window.fetch;
    if (orig.__selvanePatched) return;

    window.fetch = async function (input, init) {
      try {
        const url = typeof input === "string" ? input : (input && input.url) || "";
        if (isCartAddUrl(url) && init && init.body) {
          const body = init.body;

          // Case A: FormData
          if (body instanceof FormData) {
            const keys = [];
            for (const [k] of body.entries()) keys.push(k);
            const getterFn = (k) => (k === "__keys__" ? keys : body.get(k));

            const beforeId = normalize(body.get("id"));
            const vid = await resolveVariantId({
              nearEl: document.querySelector('form[action*="/cart/add"]') || document.body,
              handleHint: extractHandleFromPath(body.get("sections_url")) || extractHandleFromPath(window.location.pathname),
              getterFn,
            });

            if (vid && beforeId !== vid) {
              body.set("id", vid);
              log("fetch patched FormData id:", { from: beforeId, to: vid });
            }
          }

          // Case B: URLSearchParams
          else if (body instanceof URLSearchParams) {
            const keys = Array.from(body.keys());
            const getterFn = (k) => (k === "__keys__" ? keys : body.get(k));

            const beforeId = normalize(body.get("id"));
            const vid = await resolveVariantId({
              nearEl: document.body,
              handleHint: extractHandleFromPath(window.location.pathname),
              getterFn,
            });

            if (vid && beforeId !== vid) {
              body.set("id", vid);
              init.body = body; // keep
              log("fetch patched URLSearchParams id:", { from: beforeId, to: vid });
            }
          }

          // Case C: string body (querystring)
          else if (typeof body === "string") {
            const usp = parseQueryStringBody(body);
            const keys = Array.from(usp.keys());
            const getterFn = (k) => (k === "__keys__" ? keys : usp.get(k));

            const beforeId = normalize(usp.get("id"));
            const vid = await resolveVariantId({
              nearEl: document.body,
              handleHint: extractHandleFromPath(window.location.pathname),
              getterFn,
            });

            if (vid && beforeId !== vid) {
              usp.set("id", vid);
              init.body = urlSearchParamsToString(usp);
              log("fetch patched string body id:", { from: beforeId, to: vid });
            }
          }
        }
      } catch (e) {
        warn("fetch patch error:", e);
      }

      return orig.apply(this, arguments);
    };

    window.fetch.__selvanePatched = true;
    log("fetch patched");
  })();

  // ---------- 3) patch XMLHttpRequest (jQuery.ajax) ----------
  (function patchXHR() {
    const XHR = window.XMLHttpRequest;
    if (!XHR || XHR.__selvanePatched) return;

    const origOpen = XHR.prototype.open;
    const origSend = XHR.prototype.send;

    XHR.prototype.open = function (method, url) {
      try {
        this.__selvaneUrl = url;
      } catch {}
      return origOpen.apply(this, arguments);
    };

    XHR.prototype.send = function (body) {
      const url = this.__selvaneUrl || "";
      if (!isCartAddUrl(url) || !body) {
        return origSend.apply(this, arguments);
      }

      // We need async resolve; but XHR.send is sync entry.
      // Strategy: if body is FormData / URLSearchParams / string, we can patch immediately via cached variants from DOM.
      // If async is needed, we fallback (still better than nothing).
      try {
        // A) FormData
        if (body instanceof FormData) {
          const keys = [];
          for (const [k] of body.entries()) keys.push(k);
          const getterFn = (k) => (k === "__keys__" ? keys : body.get(k));

          const beforeId = normalize(body.get("id"));

          // best-effort sync: use variants from DOM only
          const variants = getVariantsFromDOMNear(document.querySelector('form[action*="/cart/add"]') || document.body);
          if (variants) {
            const opt = extractOptionsFromKeyValue(getterFn);
            const vid = matchVariantId(variants, opt.values);
            if (vid && beforeId !== vid) {
              body.set("id", vid);
              log("XHR patched FormData id:", { from: beforeId, to: vid });
            }
          } else {
            warn("XHR: no variants in DOM; cannot sync patch");
          }
        }

        // B) URLSearchParams
        else if (body instanceof URLSearchParams) {
          const keys = Array.from(body.keys());
          const getterFn = (k) => (k === "__keys__" ? keys : body.get(k));

          const beforeId = normalize(body.get("id"));
          const variants = getVariantsFromDOMNear(document.body);
          if (variants) {
            const opt = extractOptionsFromKeyValue(getterFn);
            const vid = matchVariantId(variants, opt.values);
            if (vid && beforeId !== vid) {
              body.set("id", vid);
              log("XHR patched URLSearchParams id:", { from: beforeId, to: vid });
            }
          }
        }

        // C) string body
        else if (typeof body === "string") {
          const usp = parseQueryStringBody(body);
          const keys = Array.from(usp.keys());
          const getterFn = (k) => (k === "__keys__" ? keys : usp.get(k));

          const beforeId = normalize(usp.get("id"));
          const variants = getVariantsFromDOMNear(document.body);
          if (variants) {
            const opt = extractOptionsFromKeyValue(getterFn);
            const vid = matchVariantId(variants, opt.values);
            if (vid && beforeId !== vid) {
              usp.set("id", vid);
              body = urlSearchParamsToString(usp);
              arguments[0] = body;
              log("XHR patched string body id:", { from: beforeId, to: vid });
            }
          }
        }
      } catch (e) {
        warn("XHR patch error:", e);
      }

      return origSend.apply(this, arguments);
    };

    XHR.__selvanePatched = true;
    log("XHR patched");
  })();

  log("initialized");
})();
